import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/ui/badge';
import type { TopNavBadgeDef } from './navItems';

interface NavBadgeProps {
  /** Current value; the component renders nothing for values below `1`. */
  readonly count: number;
  /** Screenreader text for an uncapped count; interpolated with `{{count}}`. */
  readonly labelKey: TopNavBadgeDef['labelKey'];
  /** Screenreader text once {@link max} is exceeded; interpolated with `{{max}}`. */
  readonly cappedLabelKey: TopNavBadgeDef['cappedLabelKey'];
  /** Highest number shown as-is; above it the badge reads `<max>+`. */
  readonly max?: number;
}

/** Cap applied when a {@link TopNavBadgeDef} does not specify its own {@link NavBadgeProps.max}. */
const DEFAULT_MAX = 99;

/**
 * Counter badge for a Top-Nav entry: a visible number, capped at `<max>+` above `max`, plus a
 * screenreader-only announcement of the full label. Renders nothing for `count < 1` — the caller
 * passes `count ?? 0`, so a still-loading or errored query never shows a stale badge.
 *
 * Carries no feature vocabulary of its own: the wording keys and the cap come from the nav item's
 * {@link TopNavBadgeDef}, so a second badge on another entry needs no change here (MR !171 review,
 * Dennis Stojanov). The first and currently only instance is the #573 open-Freigaben count.
 */
export function NavBadge({ count, labelKey, cappedLabelKey, max = DEFAULT_MAX }: NavBadgeProps) {
  const { t } = useTranslation('app');
  if (count < 1) return null;

  const capped = count > max;
  const label = capped ? t(cappedLabelKey, { max }) : t(labelKey, { count });

  return (
    <Badge variant="destructive" className="ml-1 h-5 min-w-5 justify-center px-1.5 text-xs">
      <span aria-hidden="true">{capped ? `${max}+` : count}</span>
      <span className="sr-only">{label}</span>
    </Badge>
  );
}
